/**
 * CMS visibility rule model (ASA-1570).
 *
 * Spec: "Spotlight Visibility in GoZig CMS (2026-08)" from BB product leadership.
 * OR between rules, AND between conditions inside one rule. An empty/absent field means
 * visible everywhere; rules only ever narrow an audience, they never turn an item on.
 *
 * This is a PRESENTATION filter, not a security boundary. CMS payloads are versioned and
 * edge cached, so every viewer receives the same JSON and hidden items are still in it.
 * Nothing confidential may be gated with these rules.
 */

import { z } from 'zod';

export const AGE_BUCKETS = ['13-15', '16-18', '19-24', 'over-24'] as const;
export type AgeBucket = typeof AGE_BUCKETS[number];

export const IDENTITIES = ['visitors', 'registered'] as const;
export type Identity = typeof IDENTITIES[number];

/**
 * Upper bound on an editor-typed regex. Long patterns are the cheap half of catastrophic
 * backtracking; the bound keeps a typo in Strapi from hanging the render thread.
 */
export const MAX_URL_PATTERN_LENGTH = 200;

/** A single value or a list of them — the spec allows either for every condition key. */
const oneOrMany = <T extends z.ZodTypeAny>(inner: T) =>
    z.union([inner, z.array(inner)]).transform(v => (Array.isArray(v) ? v : [v]));

const urlPattern = z.string().max(MAX_URL_PATTERN_LENGTH);
const stateCode = z.string().regex(/^[A-Za-z]{2}$/, 'expected a two letter US state code')
    .transform(s => s.toUpperCase());

/**
 * One rule. `.strict()` is load-bearing: an unknown condition key must fail the rule rather
 * than be dropped, which would silently widen the audience (ASA-1570 AC).
 */
export const VisibilityRuleSchema = z.object({
    'include-pages': oneOrMany(z.string()).optional(),
    'exclude-pages': oneOrMany(z.string()).optional(),
    'include-url': oneOrMany(urlPattern).optional(),
    'exclude-url': oneOrMany(urlPattern).optional(),
    'include-identity': oneOrMany(z.enum(IDENTITIES)).optional(),
    'exclude-identity': oneOrMany(z.enum(IDENTITIES)).optional(),
    'include-ages': oneOrMany(z.enum(AGE_BUCKETS)).optional(),
    'exclude-ages': oneOrMany(z.enum(AGE_BUCKETS)).optional(),
    'include-states': oneOrMany(stateCode).optional(),
    'exclude-states': oneOrMany(stateCode).optional(),
}).strict().refine(
    rule => Object.keys(rule).length > 0,
    { message: 'a rule needs at least one condition. To show this to everyone, leave the visibility field empty' },
);

export type VisibilityRule = z.infer<typeof VisibilityRuleSchema>;

/**
 * A rule that failed to parse. Kept (rather than dropped) so evaluation can fail closed for
 * that rule and so the editor-facing warning can name which one is broken.
 */
export interface InvalidVisibilityRule {
    index: number;
    reason: string;
}

export interface ParsedVisibility {
    /** Rules that parsed. Only these can ever match. */
    rules: VisibilityRule[];
    /** Rules that did not parse. Present only so we can warn; they never match. */
    invalid: InvalidVisibilityRule[];
    /** True when the field was absent or an empty rule list — "visible everywhere". */
    unrestricted: boolean;
}

/**
 * Schema version understood by this evaluator. The field is authored as
 * `{ "version": 1, "rules": [...] }`; a higher version means rules written against semantics
 * we do not implement, so we fail closed rather than guess at them.
 */
export const SUPPORTED_VISIBILITY_VERSION = 1;

export const UNRESTRICTED: ParsedVisibility = { rules: [], invalid: [], unrestricted: true };

/** Fail-closed result for a field that is present but not shaped like a rule list at all. */
function malformedField(reason: string): ParsedVisibility {
    return { rules: [], invalid: [{ index: -1, reason }], unrestricted: false };
}

/**
 * Pull the rule array out of the authored envelope: `{ "version": 1, "rules": [...] }`.
 * This is the shape Strapi Spotlight entries already use and the only shape we accept.
 *
 * A bare list (`[{ ... }]`, the shape the written spec shows) is NOT accepted — it is
 * reported as an error so the editor gets a warning naming the fix, rather than two shapes
 * drifting apart in the CMS. Returning a reason means the field is present but unusable.
 */
function unwrapRuleList(value: unknown): { rules: unknown[] } | { reason: string } {
    if (Array.isArray(value)) {
        return { reason: 'visibility should be an object like { "version": 1, "rules": [...] }. A list on its own is not read' };
    }
    if (value == null || typeof value !== 'object') {
        return { reason: 'visibility should be an object like { "version": 1, "rules": [...] }' };
    }

    const { version, rules } = value as { version?: unknown; rules?: unknown };
    if (version !== undefined && version !== SUPPORTED_VISIBILITY_VERSION) {
        return { reason: `visibility version ${JSON.stringify(version)} is newer than this app understands (version ${SUPPORTED_VISIBILITY_VERSION})` };
    }
    if (!Array.isArray(rules)) {
        return { reason: 'visibility has no "rules" list' };
    }
    return { rules };
}

/**
 * Parse a raw `visibility` field into rules. Never throws — a malformed field yields a
 * non-matching (hidden) result rather than an exception in a render path.
 */
export function parseVisibility(value: unknown): ParsedVisibility {
    if (value == null) return UNRESTRICTED;
    if (typeof value === 'string' && value.trim() === '') return UNRESTRICTED;

    const unwrapped = unwrapRuleList(value);
    if ('reason' in unwrapped) return malformedField(unwrapped.reason);

    const raw = unwrapped.rules;
    if (raw.length === 0) return UNRESTRICTED;

    const rules: VisibilityRule[] = [];
    const invalid: InvalidVisibilityRule[] = [];

    raw.forEach((entry, index) => {
        const result = VisibilityRuleSchema.safeParse(entry);
        if (result.success) {
            rules.push(result.data);
            return;
        }
        invalid.push({
            index,
            reason: result.error.issues.map(i => `${i.path.join('.') || '(rule)'}: ${i.message}`).join('; '),
        });
    });

    return { rules, invalid, unrestricted: false };
}
